import { CorporationsEntity } from "entities/Corporations/CorporationsEntity";
import { ModuleLicensesEntity } from "entities/ModuleLicenses/ModuleLicensesEntity";
import { useCallback, useMemo } from "react";
import { ICorporation } from "../CorporationsAPIInterfaces";
import moment from "moment";
import dayjs from "dayjs";

export enum ModuleLicenseStatusEnum {
    Active = "active",
    No_Activated = "no_activated",
    Inactive = "inactive",
}
const userTimezone = moment.tz.guess();

const useCorporationsLicenses = () => {
    const { moduleLicenses } = ModuleLicensesEntity.GetModuleLicenses();
    const { activateCorporateModule } = CorporationsEntity.ActivateCorporateModuleLicenseOsornoBO();
    const { deactivateOrReactivateCorporateModule } =
        CorporationsEntity.DeactivateOrReactivateCorporateModuleLicenseOsornoBO();
    const module_license_id = useMemo(() => moduleLicenses?.[0]?.module_license_id, [moduleLicenses]);
    const module_license_name = useMemo(() => moduleLicenses?.[0]?.name, [moduleLicenses]);

    const getModuleLicenseStatus = useCallback((row: ICorporation) => {
        const moduleLicense = row.corporate_module_licenses;
        if (!moduleLicense.length) {
            return ModuleLicenseStatusEnum.No_Activated;
        }

        const now = moment.utc(new Date()).tz(userTimezone).format("MM/DD/YYYY, h:mm:ss A");
        const expiredAt = moment.utc(moduleLicense[0].expires_at).tz(userTimezone).format("MM/DD/YYYY, h:mm:ss A");

        if (dayjs(expiredAt).isBefore(now)) {
            return ModuleLicenseStatusEnum.Inactive;
        }

        return ModuleLicenseStatusEnum.Active;
    }, []);

    const activateModuleLicense = useCallback(
        async (row: ICorporation) => {
            await activateCorporateModule({
                variables: {
                    corporate_module_license: {
                        corporate_id: row.corporate_id,
                        module_license_id: moduleLicenses[0].module_license_id,
                    },
                },
            });
        },
        [activateCorporateModule, moduleLicenses],
    );
    const deactivateModuleLicense = useCallback(
        async (row: ICorporation) => {
            await deactivateOrReactivateCorporateModule({
                variables: {
                    corporate_id: row.corporate_id,
                    module_license_id: moduleLicenses[0].module_license_id,
                    expires_at: dayjs().subtract(1, "seconds").toDate(),
                },
            });
        },
        [deactivateOrReactivateCorporateModule, moduleLicenses],
    );

    const reactivateModuleLicense = useCallback(
        async (row: ICorporation) => {
            await deactivateOrReactivateCorporateModule({
                variables: {
                    corporate_id: row.corporate_id,
                    module_license_id: moduleLicenses[0].module_license_id,
                    expires_at: dayjs().add(1, "year").toDate(),
                },
            });
        },
        [deactivateOrReactivateCorporateModule, moduleLicenses],
    );

    return {
        activateModuleLicense,
        deactivateModuleLicense,
        reactivateModuleLicense,
        getModuleLicenseStatus,
        module_license_id,
        module_license_name,
    };
};

export default useCorporationsLicenses;
